<template>
  <div v-if="hasProducts" :id="textToTarget(title, 'heading-2')" class="w-full">
    <ProductCardCollection
      :block-position
      :cta="ctaLink || undefined"
      :layout
      :product-granularity="productGranularity || undefined"
      :products="products || []"
      provider="algolia"
      :subtitle="subtitle || ''"
      :title="title || ''"
      :tracking="{
        productComponent: 'RefinedProducts',
        ...tracking,
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

import type { RefinedProductsApiProps } from '@backmarket/http-api/src/api-specs-content/models/refined-products'
import ProductCardCollection from '@backmarket/nuxt-layer-recommendation/ProductCardCollection.vue'
import { useAddProductsCacheTagHeader } from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'

import type { BlockProps } from '../../models/content-block'
import { useSearchProducts } from '../../products/useSearchProducts'
import { textToTarget } from '../../utils/textToTarget'

export type RefinedProductsBlockProps = RefinedProductsApiProps & BlockProps

const props = withDefaults(defineProps<RefinedProductsBlockProps>(), {
  ctaLink: undefined,
})

// This event is common to every CMS block in order to hide the block containers
// on demand
const emits = defineEmits(['error', 'reset-error'])

const {
  data: products,
  error,
  status,
} = useSearchProducts(
  {
    productQuery: props.productQuery,
  },
  {
    immediate: true,
    server: true,
    productGranularity: props.productGranularity || undefined,
  },
)

useAddProductsCacheTagHeader({
  products: products.value || [],
})

const layout = computed(() =>
  props.productLayout === 'grid' ? 'grid' : 'carousel',
)

watch(
  products,
  () => {
    if (status?.value === 'pending') return

    if (!products.value || products.value.length === 0) {
      emits('error')

      return
    }

    emits('reset-error')
  },
  // watching immediately is necessary since we await the useSearchProducts above
  // if we don't, we will not emit an error if useSearchProducts returns an empty array for products
  { immediate: true },
)

watch(
  error,
  () => {
    if (error.value) {
      emits('error')
    }
  },
  { immediate: true },
)

const hasProducts = computed(() => products.value && products.value.length > 0)
</script>
